import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSocket } from "../context/SocketContext";
import useAuth from "./useAuth";

// WorkOrdersGateway already broadcasts these to every subscribed user; the
// dashboards just never listened. Any of them can move any dashboard number.
const WORK_ORDER_EVENTS = [
  "workOrder:list:update",
  "workOrder:list:remove",
  "workOrder:published",
];

/**
 * Keeps the analytics dashboards in step with work order changes made
 * anywhere in the app — by this user or another one — without a manual reload.
 *
 * Every dashboard query is keyed "analytics-…", so one predicate invalidates
 * the whole family. React Query refetches the mounted ones straight away and
 * marks the rest stale for their next mount.
 */
export function useAnalyticsLiveRefresh() {
  const socket = useSocket();
  const queryClient = useQueryClient();
  const { auth } = useAuth();

  useEffect(() => {
    if (!socket || !auth?.user?.id) return;

    socket.emit("subscribeUser", { userId: auth.user.id });

    const refreshAnalytics = () =>
      queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey?.[0] ?? "").startsWith("analytics-"),
      });

    WORK_ORDER_EVENTS.forEach((event) => socket.on(event, refreshAnalytics));

    // Pass the handler to off() so we only detach our own listener — a bare
    // socket.off(event) would drop every other listener for that event too.
    return () => WORK_ORDER_EVENTS.forEach((event) => socket.off(event, refreshAnalytics));
  }, [socket, auth?.user?.id, queryClient]);
}

export default useAnalyticsLiveRefresh;
